import React, { useEffect, useReducer, useState } from 'react';
import FormInput from '../FormInput';
import PageTitle from '../PageTitle';
import { useNavigate } from 'react-router-dom';
import { RoutePaths, UserInfo } from 'Constants/Common';
import { SelectInputHelper } from '../selectInput/SelectInput';
import AddressManagerReducer, {
    AddressSearchState
} from '../address/AddressManagerReducer';
import {
    AddressUpdateRequest,
    CustomerPolicies,
    SelectOpt,
    VehicleType
} from '../CommonTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { CountryApi } from '../../../api/CountryApi';
import { CommonValidation } from '../commonValidation/commonValidation';
import FindAddress, { AddressDetails } from '../address/FindAddress';
import { AddressManagerApi } from '../../../api/AddressManagerApi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { setAddress } from 'state/slices/customerSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';

const AmendAddressPage = (): JSX.Element => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const brandId = useAppSelector((s) => s.site.brandId);
    const customerAddress = useAppSelector((state) => state.customer.address);
    const customerEmail = useAppSelector((state) => state.customer.email);

    const defaultState: AddressSearchState = {
        showAddressList: false,
        showAddressForm: false,
        addressList: [],
        address: {
            addressLine1: customerAddress.addressLine1,
            addressLine2: customerAddress.addressLine2,
            townOrCity: customerAddress.townOrCity,
            region: customerAddress.region,
            country: customerAddress.country,
            postCode: customerAddress.postcode
        },
        selectedAddress: '',
        addressErrors: {},
        finding: false,
        searchPostCode: '',
        searchPostCodeError: '',
        modal: false,
        lastSearchedPostCode: '',
        addressEditIndex: -1,
        countries: []
    };

    const dispatchStore = useAppDispatch();

    const [state, dispatch] = useReducer(AddressManagerReducer, defaultState);

    const {
        showAddressList,
        addressList,
        address,
        selectedAddress,
        addressErrors,
        finding,
        searchPostCode,
        searchPostCodeError,
        lastSearchedPostCode,
        countries
    } = state;

    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const [newCountry, setNewCountry] = useState(address.country);

    const onCancelClicked = () => {
        navigate(RoutePaths.PersonalDetails);
    };

    const validateAddress = (addressToValidate: AddressDetails) => {
        let isValid = true;
        let errors: AddressDetails = {};

        const addressLine1CharLimit = 250;
        const addressLine2CharLimit = 250;
        const postCodeCharLimit = 20;
        const townOrCityCharLimit = 100;

        if (CommonValidation.isEmpty(addressToValidate?.addressLine1)) {
            errors = {
                ...errors,
                addressLine1: t('address_line_1_required_error')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.addressLine1,
                    addressLine1CharLimit
                )
            ) {
                errors = {
                    ...errors,
                    addressLine1: t('too_many_characters_error', {
                        charLimit: addressLine1CharLimit
                    })
                };
                isValid = false;
            }
        }

        if (
            !CommonValidation.isEmpty(addressToValidate?.addressLine2) &&
            !CommonValidation.isValidLength(
                addressToValidate?.addressLine2,
                addressLine2CharLimit
            )
        ) {
            errors = {
                ...errors,
                addressLine2: t('too_many_characters_error', {
                    charLimit: addressLine2CharLimit
                })
            };
            isValid = false;
        }

        if (CommonValidation.isEmpty(addressToValidate?.postCode)) {
            errors = {
                ...errors,
                postCode: t('post_code_is_required_error')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.postCode,
                    postCodeCharLimit
                )
            ) {
                errors = {
                    ...errors,
                    postCode: t('too_many_characters_error', {
                        charLimit: postCodeCharLimit
                    })
                };
                isValid = false;
            }
        }

        if (CommonValidation.isEmpty(addressToValidate?.townOrCity)) {
            errors = {
                ...errors,
                townOrCity: t('town_city_required')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.townOrCity,
                    townOrCityCharLimit
                )
            ) {
                errors = {
                    ...errors,
                    townOrCity: t('too_many_characters_error', {
                        charLimit: townOrCityCharLimit
                    })
                };
                isValid = false;
            }
        }

        if (SelectInputHelper.NullValue() == addressToValidate?.country) {
            errors = {
                ...errors,
                country: t('country_is_required')
            };
            isValid = false;
        }

        dispatch({
            type: 'set-address-errors',
            errors
        });
        return isValid;
    };

    const onChangeSearchPostCode = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        dispatch({
            type: 'set-search-postcode',
            postcode: event.currentTarget.value
        });
    };

    const onCountryChange = (country: string) => {
        const newAddress = {
            ...address,
            country: country ?? SelectInputHelper.NullValue()
        };
        dispatch({
            type: 'set-address',
            address: newAddress
        });

        setNewCountry(country);
    };

    useEffect(() => {
        populateCountries();
    }, []);

    const onChangeDetail = (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => {
        const newAddress = {
            ...address,
            [key]: event.currentTarget.value ?? ''
        };
        dispatch({
            type: 'set-address',
            address: newAddress
        });
    };

    const populateCountries = async () => {
        const successCallback = (result) => {
            const countryOptions = getCountryOptions(result);

            dispatch({
                type: 'set-countries',
                countries: countryOptions
            });
        };

        const failureCallback = () => {
            console.log('Failed to load countries');
        };

        const token = await getAccessTokenSilently();

        new CountryApi().loadCountries(token, successCallback, failureCallback);
    };

    const getCountryOptions = (lookupItems) => {
        const items = [];

        if (lookupItems === undefined || lookupItems === null) return items;

        lookupItems.sort();

        lookupItems.map((item) => {
            const lookupOption = {
                id: item.iso3code,
                text: item.countryName,
                value: item.countryName,
                disabled: false
            };
            items.push(lookupOption);
        });

        return items;
    };

    const onFindClick = async () => {
        const isValid = validatePostcode();

        if (isValid) {
            if (lastSearchedPostCode !== searchPostCode) {
                dispatch({
                    type: 'set-finding',
                    finding: true
                });

                const token = await getAccessTokenSilently();

                new AddressManagerApi().loadAddresses(
                    searchPostCode,
                    token,
                    loadAddressesSuccess,
                    loadAddressesFailed
                );
            }
        }
    };

    const truncate = (stringToTruncate, maxLength) => {
        return stringToTruncate.length > maxLength
            ? stringToTruncate.substr(0, maxLength - 1) + '...'
            : stringToTruncate;
    };

    const getLookupItemsAsOptions = (lookupItems, includeSelect) => {
        const items: SelectOpt[] = [];

        const option: SelectOpt = {
            id: '0',
            text: t('select_default'),
            value: SelectInputHelper.NullValue(),
            disabled: false
        };

        if (includeSelect) {
            items.push(option);
        }

        const maxAddressLength = 35;

        lookupItems.map((item, index) => {
            const lookupOption = {
                id: index + 1,
                text: truncate(item.text, maxAddressLength),
                value: item.value,
                disabled: false
            };
            items.push(lookupOption);
        });

        return items;
    };

    const loadAddressesSuccess = (addresses) => {
        const addressOptions = getLookupItemsAsOptions(addresses, true);
        dispatch({
            type: 'load-addresses',
            addresses: addressOptions,
            show: true,
            error: ''
        });
    };

    const loadAddressesFailed = (error) => {
        console.log(`Find address error: ${error}`);
        dispatch({
            type: 'load-addresses',
            addresses: [],
            show: false,
            error: t('address_not_found_error')
        });
        //this function does not reset the address textbox values in case the user has typed them and clicked the find address by mistake
    };

    const validatePostcode = () => {
        let isValid = true;
        const postCodeCharLimit = 20;

        dispatch({
            type: 'set-address-errors',
            errors: {}
        });

        if (CommonValidation.isEmpty(searchPostCode)) {
            dispatch({
                type: 'set-search-postcode-error',
                error: t('post_code_required_error')
            });
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    searchPostCode,
                    postCodeCharLimit
                )
            ) {
                isValid = false;
                dispatch({
                    type: 'set-search-postcode-error',
                    error: t('too_many_characters_error', {
                        charLimit: postCodeCharLimit
                    })
                });
            }
        }
        return isValid;
    };

    const updateAddress = async (data) => {
        const controller = new AbortController();

        const successCallback = (result) => {
            setSubmitButtonDisabled(false);

            if (result && result.success) {
                toast.success(t('success_to_submit_address'), {
                    onClose: () => {
                        dispatchStore(
                            setAddress({
                                id: crypto.randomUUID(),
                                addressLine1: address.addressLine1,
                                addressLine2: address.addressLine2,
                                country: address.country,
                                townOrCity: address.townOrCity,
                                region: address.region,
                                postcode: address.postCode
                            })
                        );
                        const userKey = `${user?.sub || ''}_${brandId}`;
                        localStorage.setItem(
                            `${userKey}_${UserInfo.Postcode}`,
                            address.postCode
                        );
                        navigate(RoutePaths.PersonalDetails);
                    }
                });
            } else {
                showErrorMessage();
            }
        };

        const failureCallback = () => {
            showErrorMessage();
            setSubmitButtonDisabled(false);
        };

        const showErrorMessage = () => {
            toast.error(t('failed_to_submit_address'));
        };

        const token = await getAccessTokenSilently();

        new AddressManagerApi().UpdateAddress(
            data,
            token,
            successCallback,
            failureCallback,
            controller.signal,
            { skipToastNotification: true }
        );

        return () => {
            controller.abort();
        };
    };

    const getCustomerPoliciesPerVehicle = (
        v: VehicleType
    ): CustomerPolicies => ({
        customerEmail,
        vehicleRegNumber: v.vrn,
        productRefNumbers: v.policies.map((p) => p.reference)
    });

    const onSubmit = () => {
        if (validateAddress(address)) {
            setSubmitButtonDisabled(true);

            const customerPolicies = vehicles.map(
                getCustomerPoliciesPerVehicle
            );

            const data: AddressUpdateRequest = {
                policies: customerPolicies,
                address: {
                    firstLine: address.addressLine1,
                    secondLine: address.addressLine2,
                    city: address.townOrCity,
                    county: address.region,
                    postcode: address.postCode,
                    countryIsoId: countries.find((c) => c.text === newCountry)
                        .id,
                    provinceId: 0
                }
            };

            updateAddress(data);
        }
    };

    const resetAddress = () => {
        setNewCountry('');
        dispatch({
            type: 'set-address',
            address: {
                addressLine1: '',
                addressLine2: '',
                townOrCity: '',
                region: '',
                country: '',
                postCode: ''
            }
        });
    };

    const setAddressSuccess = (foundAddress) => {
        if (foundAddress) {
            const country = countries.find(
                (c) => c.text === foundAddress.country.toLowerCase()
            );

            dispatch({
                type: 'set-address',
                address: {
                    addressLine1: foundAddress.addressLine1,
                    addressLine2: foundAddress.addressLine2,
                    townOrCity: foundAddress.townOrCity,
                    region: foundAddress.region,
                    country: CommonValidation.isNotEmpty(country.value)
                        ? country.value
                        : SelectInputHelper.NullValue(), //get the user to select the country
                    postCode: foundAddress.postcode
                }
            });
            setAddressFormVisible();
        } else {
            setAddressFailedRoutine();
        }
    };

    const setAddressFailed = (error) => {
        console.log(`Find address error: ${error}`);
        setAddressFailedRoutine();
    };

    const onSelectAddress = async (selectedValue: string) => {
        dispatch({
            type: 'set-selected-address',
            moniker: selectedValue
        });

        if (SelectInputHelper.isNullValue(selectedValue)) {
            //clicing Select... in the address list will clear the address textbox values
            resetAddress();
            setAddressFormVisible();
        } else {
            const token = await getAccessTokenSilently();

            new AddressManagerApi().loadAddress(
                selectedValue,
                token,
                setAddressSuccess,
                setAddressFailed
            );
        }
    };

    const setAddressFormVisible = () => {
        dispatch({
            type: 'set-show-address-form',
            show: true
        });
    };

    const setAddressFailedRoutine = () => {
        resetAddress();
        setAddressFormVisible();
    };

    return (
        <div className='card page-content'>
            <PageTitle title={t('amend_address_title')} />
            <FormInput id='amendpolicyholderinfo' label={t('address_details')}>
                <FindAddress
                    showAddressForm={true}
                    showAddressList={showAddressList}
                    selectedAddress={selectedAddress}
                    addressErrors={addressErrors}
                    addressList={addressList}
                    address={address}
                    searchPostCode={searchPostCode}
                    searchPostCodeError={searchPostCodeError}
                    finding={finding}
                    countries={countries}
                    onSelectAddress={onSelectAddress}
                    onChangeDetail={onChangeDetail}
                    onShowAddressFormClick={setAddressFormVisible}
                    onFindClick={onFindClick}
                    onChangeSearchPostCode={onChangeSearchPostCode}
                    onCountryChange={onCountryChange}
                />
            </FormInput>
            <div className='bottom-navigation'>
                <button
                    disabled={submitButtonDisabled}
                    name='btnSubmit'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onSubmit}>
                    {t('submit')}
                </button>
                <button
                    name='btnCancel'
                    data-testid='secondary-button'
                    className='btn btn-secondary progress-button'
                    onClick={onCancelClicked}>
                    {t('cancel')}
                </button>
            </div>
        </div>
    );
};

export default AmendAddressPage;
